import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import DOMPurify from 'dompurify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sanitizeContent = (raw) => {
  if (!raw) return '';
  return DOMPurify.sanitize(raw.replace(/\n/g, '<br><br>'), { USE_PROFILES: { html: true } });
};

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchArticle = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/articles/${slug}`);
      setArticle(data);
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFAF4] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E7A6A] mx-auto"></div>
          <p className="mt-4 text-[#332724]">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FDFAF4] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#332724] text-lg mb-4">Article non trouvé</p>
          <Link to="/conseils" className="text-[#1E7A6A] hover:underline">
            Retour aux conseils
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFAF4] pb-16">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link
          to="/conseils"
          className="inline-flex items-center gap-2 text-[#1E7A6A] hover:gap-3 transition-all mb-8"
          data-testid="back-to-conseils"
        >
          <ArrowLeft size={20} />
          <span className="text-sm uppercase tracking-[0.2em]">Retour aux conseils</span>
        </Link>

        {article.image_url && (
          <div className="h-96 mb-8 rounded-lg overflow-hidden">
            <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        <p className="text-xs uppercase tracking-[0.2em] text-[#B8922A] mb-4">{article.category}</p>

        <h1
          className="text-4xl md:text-5xl font-light mb-6 text-[#5C2018]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          data-testid="article-title"
        >
          {article.title}
        </h1>

        <div className="flex items-center gap-6 text-sm text-[#332724]/70 mb-8 pb-8 border-b border-[#B8922A]/20">
          <div className="flex items-center gap-2">
            <User size={16} />
            <span>{article.author}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={16} />
            <span>{formatDate(article.date)}</span>
          </div>
        </div>

        <div
          className="prose prose-lg max-w-none text-[#332724] font-light leading-relaxed"
          dangerouslySetInnerHTML={{ __html: sanitizeContent(article.content) }}
          data-testid="article-content"
        />
      </div>
    </div>
  );
};

export default ArticlePage;
